// /////////////////////////////////////////////////////////////
// [ファイル] CustomerEmploymentHistoryList.tsx
// [概要] 個人顧客の勤務先変遷履歴 (trn_customer_employment_history) の一覧と手修正。
//        - 現職行は顧客の勤務先法人 (employer_id) と同期されるため、ここでは勤務先そのものは変えない
//          (勤務先の変更は顧客編集フォームで行い、サーバー側が旧行を閉じて新行を開く)。
//        - 手修正できるのは 在籍開始日 / 終了日 / 部署 / 役職 / メモ。過去行のみ削除可。
//        - 日付は判明日ベースで自動記録されるため、正確な日付が分かればここで直す。
// /////////////////////////////////////////////////////////////
import React, { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Link,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import axios from 'axios';
import * as Icons from '../constants/icons';
import { STANDARD_CTA_BUTTON_SX, STANDARD_TEXT_BUTTON_SX } from '../constants/buttonStyles';
import { useAuth } from '../firebase/AuthProvider';
import { useConfirm } from '../providers/ConfirmProvider';
import { useNotification } from '../providers/NotificationProvider';
import { ep } from '../api/endpoints';

const UPDATE_URL = ep('update_crm_employment_history');
const DELETE_URL = ep('delete_crm_employment_history');

export interface EmploymentHistory {
  id: number;
  employer_id: string;
  employer_name?: string | null;
  employer_branch_name?: string | null;
  department?: string | null;
  occupation?: string | null;
  start_date?: string | null;
  end_date?: string | null;
  is_current: boolean;
  source?: string | null;
  note?: string | null;
}

interface Props {
  customerId: string;
  history: EmploymentHistory[];
  onChanged?: () => void;
  onOpenEmployer?: (employerId: string) => void;
}

const SOURCE_LABELS: Record<string, string> = {
  manual: '',
  backfill: '初期投入',
  event_participant: 'イベント名簿',
  merge: '統合',
  detach: '一覧から外す',
};

const fmtDate = (d?: string | null) => (d ? d.replace(/-/g, '/') : '');
const period = (h: EmploymentHistory) => {
  const s = fmtDate(h.start_date);
  const e = h.is_current ? '現在' : fmtDate(h.end_date);
  if (!s && !e) return '期間不明';
  return `${s || '不明'} 〜 ${e || '不明'}`;
};
const employerLabel = (h: EmploymentHistory) =>
  `${h.employer_name || '(法人名不明)'}${h.employer_branch_name ? ' ' + h.employer_branch_name : ''}`;

const CustomerEmploymentHistoryList: React.FC<Props> = ({ customerId, history, onChanged, onOpenEmployer }) => {
  const { user } = useAuth();
  const confirm = useConfirm();
  const notify = useNotification();
  const [editing, setEditing] = useState<EmploymentHistory | null>(null);
  const [form, setForm] = useState<Partial<EmploymentHistory>>({});
  const [saving, setSaving] = useState(false);

  const list = Array.isArray(history) ? history : [];

  const openEdit = (h: EmploymentHistory) => {
    setEditing(h);
    setForm({
      start_date: h.start_date || '',
      end_date: h.end_date || '',
      department: h.department || '',
      occupation: h.occupation || '',
      note: h.note || '',
    });
  };

  const handleSave = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      const res = await axios.post(UPDATE_URL, {
        id: editing.id,
        customerId,
        data: {
          start_date: form.start_date || null,
          // 現職行の終了日は常に空 (閉じるのは勤務先変更時にサーバーが行う)
          end_date: editing.is_current ? null : form.end_date || null,
          department: form.department || null,
          occupation: form.occupation || null,
          note: form.note || null,
        },
        operator_id: user?.email,
      });
      if (res.data.status === 'success') {
        notify.success('勤務先履歴を更新しました');
        setEditing(null);
        onChanged?.();
      } else {
        notify.error(res.data.message || '更新に失敗しました');
      }
    } catch (err: any) {
      notify.error(err?.response?.data?.message || '更新に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (h: EmploymentHistory) => {
    const ok = await confirm({
      title: '勤務先履歴を削除',
      message: `「${employerLabel(h)}」(${period(h)}) の履歴を削除しますか？\n誤登録の取り消し用です。顧客情報そのものは変わりません。`,
      destructive: true,
      confirmText: '削除する',
      cancelText: 'キャンセル',
    });
    if (!ok) return;
    try {
      const res = await axios.post(DELETE_URL, { id: h.id, customerId, operator_id: user?.email });
      if (res.data.status === 'success') {
        notify.success('勤務先履歴を削除しました');
        onChanged?.();
      } else {
        notify.error(res.data.message || '削除に失敗しました');
      }
    } catch (err: any) {
      notify.error(err?.response?.data?.message || '削除に失敗しました');
    }
  };

  if (list.length === 0) {
    return (
      <Typography variant="body2" color="text.disabled" sx={{ py: 0.5 }}>
        勤務先履歴はありません（勤務先法人を登録すると自動で記録されます）
      </Typography>
    );
  }

  return (
    <Box>
      {list.map((h) => (
        <Box
          key={h.id}
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1,
            py: 0.6,
            borderBottom: '1px dashed',
            borderColor: 'divider',
            '&:last-of-type': { borderBottom: 'none' },
          }}
        >
          <Icons.Business sx={{ fontSize: 15, color: h.is_current ? 'primary.main' : 'text.disabled', mt: '3px' }} />
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
              {onOpenEmployer ? (
                <Link
                  component="button"
                  type="button"
                  variant="body2"
                  onClick={() => onOpenEmployer(h.employer_id)}
                  sx={{ fontWeight: h.is_current ? 600 : 400, textAlign: 'left' }}
                >
                  {employerLabel(h)}
                </Link>
              ) : (
                <Typography variant="body2" sx={{ fontWeight: h.is_current ? 600 : 400 }}>
                  {employerLabel(h)}
                </Typography>
              )}
              {h.is_current && <Chip label="現職" size="small" color="primary" sx={{ height: 18, fontSize: '0.65rem' }} />}
              {(h.department || h.occupation) && (
                <Typography variant="caption" color="text.secondary">
                  {[h.department, h.occupation].filter(Boolean).join(' / ')}
                </Typography>
              )}
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              {period(h)}
              {h.source && SOURCE_LABELS[h.source] ? `　（${SOURCE_LABELS[h.source]}）` : ''}
              {h.note ? `　${h.note}` : ''}
            </Typography>
          </Box>
          <Tooltip title="期間・メモを修正">
            <IconButton size="small" onClick={() => openEdit(h)}>
              <Icons.Edit sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          {!h.is_current && (
            <Tooltip title="この履歴を削除">
              <IconButton size="small" color="error" onClick={() => handleDelete(h)}>
                <Icons.Delete sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ))}

      <Dialog open={!!editing} onClose={() => !saving && setEditing(null)} maxWidth="xs" fullWidth disableScrollLock>
        <DialogTitle sx={{ fontSize: '0.875rem', py: 0.75, display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Icons.Business sx={{ fontSize: 18 }} />
          勤務先履歴の修正
        </DialogTitle>
        <DialogContent dividers>
          {editing && (
            <Grid container spacing={1.5} sx={{ pt: 0.5 }}>
              <Grid size={12}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{employerLabel(editing)}</Typography>
                <Typography variant="caption" color="text.secondary">
                  勤務先そのものを変える場合は顧客編集で勤務先法人を変更してください（旧行が自動で閉じます）
                </Typography>
              </Grid>
              <Grid size={6}>
                <TextField
                  label="在籍開始日"
                  type="date"
                  size="small"
                  fullWidth
                  value={form.start_date || ''}
                  onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                  slotProps={{ inputLabel: { shrink: true } }}
                />
              </Grid>
              <Grid size={6}>
                <TextField
                  label="在籍終了日"
                  type="date"
                  size="small"
                  fullWidth
                  disabled={editing.is_current}
                  value={editing.is_current ? '' : form.end_date || ''}
                  onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                  slotProps={{ inputLabel: { shrink: true } }}
                  helperText={editing.is_current ? '現職のため空欄' : undefined}
                />
              </Grid>
              <Grid size={6}>
                <TextField
                  label="部署"
                  size="small"
                  fullWidth
                  value={form.department || ''}
                  onChange={(e) => setForm({ ...form, department: e.target.value })}
                />
              </Grid>
              <Grid size={6}>
                <TextField
                  label="役職"
                  size="small"
                  fullWidth
                  value={form.occupation || ''}
                  onChange={(e) => setForm({ ...form, occupation: e.target.value })}
                />
              </Grid>
              <Grid size={12}>
                <TextField
                  label="メモ"
                  size="small"
                  fullWidth
                  multiline
                  minRows={2}
                  value={form.note || ''}
                  onChange={(e) => setForm({ ...form, note: e.target.value })}
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditing(null)} disabled={saving} sx={STANDARD_TEXT_BUTTON_SX}>
            キャンセル
          </Button>
          <Button onClick={handleSave} disabled={saving} startIcon={<Icons.Save />} sx={STANDARD_CTA_BUTTON_SX}>
            保存する
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CustomerEmploymentHistoryList;
